import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Spin, Alert } from "antd";
import type { Scholarship } from "~/models/Scholarship";
import SaveDeadlineToCalendar from "./SaveDeadlineToCalendar";
import ScholarshipsAPI from "~/services/ScholarshipsAPI";
import { Link } from "react-router-dom";

// ScholarshipCard Component
const ScholarshipCard = ({ scholarship }: { scholarship: Scholarship }) => {
  return (
    <Card
      title={<Link to={`/scholarship/${scholarship.slug}`}>
      {scholarship.name}
      </Link>}
      className="w-full max-w-md shadow-md rounded-2xl mb-4"
      cover={
        <img
          alt={scholarship.name}
          src={scholarship.img_url}
          className="p-4"
        />
      }
    >
      <p className="text-gray-600 mb-4">{scholarship.description}</p>
      <SaveDeadlineToCalendar scholarship={scholarship} />
    </Card>
  );
};

// ScholarshipsView Component
export default function ScholarshipsView() {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalResults, setTotalResults] = useState<number | null>(null);
  const fetchedPages = useRef<Set<number>>(new Set());

  const fetchScholarships = async (page: number) => {
    console.log("Fetching page:", page);

    // skip if already fetched this page
    if (fetchedPages.current.has(page)) return;
    setLoading(true);
    try {
      fetchedPages.current.add(page);

      const response = await ScholarshipsAPI.searchScholarships({}, page);
      const newData: Scholarship[] = response.data.data;
      totalResults === null && setTotalResults(response.data.length);

      setScholarships((prev) => [...prev, ...newData]);

      // if API tells us no more data
      console.log("Total Results:", totalResults, "Current Length:", scholarships.length + newData.length);
      if (totalResults && (scholarships.length + newData.length) >= totalResults || totalResults === 0) {
        setHasMore(false);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load scholarships");
    } finally {
      setLoading(false);
    }
  };

  // fetch first page on mount
  useEffect(() => {
    fetchScholarships(pageNumber);
  }, [pageNumber]);

  return (
    <div className="my-8" style={{ padding: "24px" }}>
      {error && <Alert type="error" message={error} />}
      <h1 className="text-3xl font-bold mb-6">
        Scholarships {totalResults !== null && `(${totalResults} found)`}
      </h1>
      <div
        style={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        }}
      >
        {scholarships.map((scholarship) => (
          <ScholarshipCard key={scholarship.id} scholarship={scholarship} />
        ))}
      </div>

      <div className="flex justify-center mt-6">
        {loading ? (
          <Spin />
        ) : (
          hasMore && (
            <Button
              type="primary"
              onClick={() => setPageNumber((prev) => prev + 1)}
            >
              Load More
            </Button>
          )
        )}
      </div>
    </div>
  );
}
